"use client";

import { CartesianGrid, Line, LineChart, XAxis, YAxis } from "recharts";

import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";

import { formatBRL } from "@/helper/currency";
import { chartProfitConfig } from "@/constants";
import { cn } from "@/lib/utils";

interface Projection {
  mes: string | number;
  valor_gasto_total: number;
  valor_lucro_liquido_sozinho: number;
  valor_lucro_liquido_com_corretor: number;
  roi_solo: number;
  roi_com_corretor: number;
  valor_investimento_selic: number;
  retorno_selic: number;
  [key: string]: any;
}

interface ProfitChartProps {
  data: Projection[];
  className?: string;
}

export const ProfitChart = ({ data, className }: ProfitChartProps) => {
  const formattedData = data.map((item) => ({
    ...item,
    mes: typeof item.mes === "number" ? `Mês ${item.mes}` : item.mes,
  }));

  return (
    <ChartContainer
      config={chartProfitConfig}
      className={cn("w-full h-[220px]", className)}
    >
      <LineChart accessibilityLayer data={formattedData} margin={{ left: 40 }}>
        <CartesianGrid vertical={false} />
        <YAxis
          tickLine={false}
          axisLine={false}
          tickFormatter={formatBRL}
          domain={["dataMin", "dataMax"]}
        />
        <XAxis dataKey="mes" tickLine={false} axisLine={false} tickMargin={8} />
        <ChartTooltip
          content={
            <ChartTooltipContent
              hideLabel
              className="min-w-[200px]"
              formatter={(value, name, item, index) => (
                <div className="flex flex-col gap-3">
                  {index === 0 ? <p>{item.payload.mes}</p> : null}
                  <div className="flex gap-4 items-center justify-between w-full">
                    <div className="flex gap-2 items-center justify-between">
                      <div
                        className="h-2.5 w-2.5 shrink-0 rounded-[2px] bg-[--color-bg]"
                        style={
                          {
                            "--color-bg": `var(--color-${name})`,
                          } as React.CSSProperties
                        }
                      />
                      <span>
                        {chartProfitConfig[
                          name as keyof typeof chartProfitConfig
                        ]?.label || name}
                      </span>
                    </div>
                    <div className="font-mono font-medium text-foreground">
                      {formatBRL(value as number)}
                    </div>
                  </div>
                  {index === 3 && (
                    <div className="flex flex-col gap-1 mt-4 pt-2 border-t">
                      <p>
                        ROI sozinho:{" "}
                        <span className="font-mono font-medium text-foreground">
                          {item.payload.roi_solo}%
                        </span>
                      </p>
                      <p>
                        ROI com corretor:{" "}
                        <span className="font-mono font-medium text-foreground">
                          {item.payload.roi_com_corretor}%
                        </span>
                      </p>
                    </div>
                  )}
                </div>
              )}
            />
          }
          cursor={false}
          defaultIndex={1}
        />
        <ChartLegend content={<ChartLegendContent />} />
        <Line
          dataKey="valor_gasto_total"
          type="monotone"
          stroke="var(--color-valor_gasto_total)"
          strokeWidth={2}
          dot={false}
        />
        <Line
          dataKey="valor_lucro_liquido_sozinho"
          type="monotone"
          stroke="var(--color-valor_lucro_liquido_sozinho)"
          strokeWidth={2}
          dot={false}
        />
        <Line
          dataKey="valor_lucro_liquido_com_corretor"
          type="monotone"
          stroke="var(--color-valor_lucro_liquido_com_corretor)"
          strokeWidth={2}
          dot={false}
        />
        <Line
          dataKey="valor_investimento_selic"
          type="monotone"
          stroke="var(--color-valor_investimento_selic)"
          strokeWidth={2}
          dot={false}
        />
      </LineChart>
    </ChartContainer>
  );
};
